"use client";

import type { TranslateAudioPage, TranslateVideoPage } from "@mw/shared/payload-types";
import Link from "next/link";
import DynamicFlag from "@ui/components/languages/DynamicFlag";
import { ArrowRight } from "lucide-react";

type TranslatePage = TranslateAudioPage | TranslateVideoPage;

interface PageWithLabels {
  page: TranslatePage;
  fromLanguageLabel: string;
  toLanguageLabel: string;
  fromLanguageCode: string;
  toLanguageCode: string;
}

interface RelatedTranslatePagesGridProps {
  pagesWithLabels: PageWithLabels[];
  mode: "audio" | "video";
}

export function RelatedTranslatePagesGrid({ pagesWithLabels, mode }: RelatedTranslatePagesGridProps) {
  if (pagesWithLabels.length === 0) {
    return null;
  }

  const parentPath = mode === "audio" ? "/translate-audio" : "/translate-video";

  return (
    <div className='m-1 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3'>
      {pagesWithLabels.map(({ page, fromLanguageLabel, toLanguageLabel, fromLanguageCode, toLanguageCode }) => {
        return (
          <Link
            key={page.id}
            href={`${parentPath}/${page.slug}`}
            className='effect-color group relative flex items-center gap-16 overflow-hidden rounded-xl bg-eggshell px-24 py-24 transition-shadow duration-300 ease-out hover:bg-default hover:shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)]'
          >
            <div className='flex items-center gap-8'>
              <DynamicFlag locale={fromLanguageCode} size={24} alt={`${fromLanguageLabel} ${mode}`} />
              <span className='f-paragraph-02 text-default transition-colors duration-300'>{fromLanguageLabel}</span>
            </div>
            <ArrowRight className='size-20 text-light transition-all duration-300 group-hover:translate-x-4 group-hover:text-default' />
            <div className='flex items-center gap-8'>
              <DynamicFlag locale={toLanguageCode} size={24} alt={`${toLanguageLabel} ${mode}`} />
              <span className='f-paragraph-02 text-default transition-colors duration-300'>{toLanguageLabel}</span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
